import { useMemo, useState, useEffect, useCallback } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { HiMenu, HiX, HiSearch, HiChevronRight } from 'react-icons/hi'
import SEO from '../../components/SEO/SEO'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import {
  MSBI_DOC_GROUPS,
  MSBI_DOCS,
  MSBI_DOC_IDS,
  MSBI_DEFAULT_DOC_ID,
  MSBI_BADGE_LABELS,
  getMsbiDocContent,
  getMsbiDocMeta,
  extractMsbiHeadings,
} from '../../data/msbiProjects'
import './MsbiProjects.css'

const markdownComponents = (getLocalizedLink, currentDocId) => ({
  table: ({ children, ...props }) => (
    <div className="msbi-table-scroll">
      <table {...props}>{children}</table>
    </div>
  ),
  a: ({ href, children, ...props }) => {
    if (href && /\.md(\#.*)?$/i.test(href)) {
      const [filePart, hash] = href.split('#')
      const slug = filePart.replace(/^\.\//, '').replace(/\.md$/i, '')
      const target =
        MSBI_DOC_IDS.has(slug)
          ? getLocalizedLink(`/internal/msbi-projects/${slug}${hash ? `#${hash}` : ''}`)
          : getLocalizedLink(`/internal/msbi-projects/${currentDocId}`)
      return <Link to={target}>{children}</Link>
    }
    if (href?.startsWith('http')) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      )
    }
    return (
      <a href={href} {...props}>
        {children}
      </a>
    )
  },
  h2: ({ children, ...props }) => {
    const text = String(children)
    const id = text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-')
    return (
      <h2 id={id} {...props}>
        {children}
      </h2>
    )
  },
  h3: ({ children, ...props }) => {
    const text = String(children)
    const id = text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-')
    return (
      <h3 id={id} {...props}>
        {children}
      </h3>
    )
  },
})

function MsbiProjects() {
  const { docId } = useParams()
  const getLocalizedLink = useLocalizedLink()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeHeading, setActiveHeading] = useState('')

  const needsRedirect = !docId || (docId && !MSBI_DOC_IDS.has(docId))
  const activeDocId = docId && MSBI_DOC_IDS.has(docId) ? docId : MSBI_DEFAULT_DOC_ID

  const docMeta = getMsbiDocMeta(activeDocId)
  const markdown = getMsbiDocContent(activeDocId)
  const headings = useMemo(() => extractMsbiHeadings(markdown), [markdown])

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return MSBI_DOC_GROUPS
    return MSBI_DOC_GROUPS.map((group) => ({
      ...group,
      docs: group.docs.filter(
        (doc) =>
          doc.title.toLowerCase().includes(q) ||
          doc.subtitle.toLowerCase().includes(q) ||
          doc.id.toLowerCase().includes(q)
      ),
    })).filter((group) => group.docs.length > 0)
  }, [query])

  const components = useMemo(
    () => markdownComponents(getLocalizedLink, activeDocId),
    [getLocalizedLink, activeDocId]
  )

  const closeSidebar = useCallback(() => setSidebarOpen(false), [])

  useEffect(() => {
    closeSidebar()
    setActiveHeading('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeDocId, closeSidebar])

  useEffect(() => {
    if (!headings.length) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]?.target?.id) {
          setActiveHeading(visible[0].target.id)
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.25, 0.5, 1] }
    )

    headings.forEach(({ slug }) => {
      const el = document.getElementById(slug)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [headings, activeDocId])

  const docIndex = MSBI_DOCS.findIndex((d) => d.id === activeDocId)
  const prevDoc = docIndex > 0 ? MSBI_DOCS[docIndex - 1] : null
  const nextDoc = docIndex < MSBI_DOCS.length - 1 ? MSBI_DOCS[docIndex + 1] : null

  if (needsRedirect) {
    return (
      <Navigate
        to={getLocalizedLink(`/internal/msbi-projects/${MSBI_DEFAULT_DOC_ID}`)}
        replace
      />
    )
  }

  return (
    <div className="msbi-projects-page">
      <SEO
        title={`МСБИ · ${docMeta?.title ?? 'Проекты документов'}`}
        description="Внутренний просмотр проектных нормативных документов МСБИ"
        noindex
      />

      <div className="msbi-draft-banner" role="status">
        <span className="msbi-draft-banner__label">Черновик</span>
        Проектные документы МСБИ — только для внутреннего ознакомления, не для публикации
      </div>

      <div className="msbi-projects-shell">
        <button
          type="button"
          className="msbi-sidebar-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-expanded={sidebarOpen}
          aria-controls="msbi-sidebar"
        >
          {sidebarOpen ? <HiX aria-hidden /> : <HiMenu aria-hidden />}
          <span>Документы</span>
        </button>

        {sidebarOpen && (
          <button
            type="button"
            className="msbi-sidebar-backdrop"
            aria-label="Закрыть меню"
            onClick={closeSidebar}
          />
        )}

        <aside id="msbi-sidebar" className={`msbi-sidebar ${sidebarOpen ? 'msbi-sidebar--open' : ''}`}>
          <div className="msbi-sidebar__header">
            <Link to={getLocalizedLink('/')} className="msbi-sidebar__home">
              ISUMA
            </Link>
            <h1 className="msbi-sidebar__title">Пакет МСБИ</h1>
            <p className="msbi-sidebar__hint">15 документов · v1.1</p>
          </div>

          <div className="msbi-search">
            <HiSearch className="msbi-search__icon" aria-hidden />
            <input
              type="search"
              className="msbi-search__input"
              placeholder="Поиск документа…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Поиск документа"
            />
          </div>

          <nav className="msbi-nav" aria-label="Документы МСБИ">
            {filteredGroups.map((group) => (
              <div key={group.id} className="msbi-nav__group">
                <h2 className="msbi-nav__group-label">{group.label}</h2>
                <ul className="msbi-nav__list">
                  {group.docs.map((doc) => {
                    const isActive = doc.id === activeDocId
                    return (
                      <li key={doc.id}>
                        <Link
                          to={getLocalizedLink(`/internal/msbi-projects/${doc.id}`)}
                          className={`msbi-nav__link ${isActive ? 'msbi-nav__link--active' : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span className="msbi-nav__link-title">{doc.title}</span>
                          <span className="msbi-nav__link-sub">{doc.subtitle}</span>
                          <span className={`msbi-badge msbi-badge--${doc.badge}`}>
                            {MSBI_BADGE_LABELS[doc.badge]}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <div className="msbi-main">
          <header className="msbi-doc-header">
            <p className="msbi-doc-header__group">{docMeta?.groupLabel}</p>
            <h1 className="msbi-doc-header__title">{docMeta?.title}</h1>
            <p className="msbi-doc-header__subtitle">{docMeta?.subtitle}</p>
            {docMeta && (
              <span className={`msbi-badge msbi-badge--${docMeta.badge}`}>
                {MSBI_BADGE_LABELS[docMeta.badge]}
              </span>
            )}
          </header>

          <div className="msbi-content-grid">
            <article className="msbi-document">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {markdown}
              </ReactMarkdown>
            </article>

            {headings.length > 0 && (
              <nav className="msbi-toc" aria-label="Содержание">
                <h2 className="msbi-toc__title">На этой странице</h2>
                <ul className="msbi-toc__list">
                  {headings.map(({ level, text, slug }) => (
                    <li key={slug} className={`msbi-toc__item msbi-toc__item--h${level}`}>
                      <a
                        href={`#${slug}`}
                        className={activeHeading === slug ? 'msbi-toc__link--active' : ''}
                        onClick={(e) => {
                          e.preventDefault()
                          document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth' })
                        }}
                      >
                        {text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>

          <footer className="msbi-doc-footer">
            {prevDoc ? (
              <Link
                to={getLocalizedLink(`/internal/msbi-projects/${prevDoc.id}`)}
                className="msbi-doc-nav msbi-doc-nav--prev"
              >
                <span className="msbi-doc-nav__label">← Предыдущий</span>
                <span className="msbi-doc-nav__title">{prevDoc.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {nextDoc ? (
              <Link
                to={getLocalizedLink(`/internal/msbi-projects/${nextDoc.id}`)}
                className="msbi-doc-nav msbi-doc-nav--next"
              >
                <span className="msbi-doc-nav__label">
                  Следующий <HiChevronRight aria-hidden />
                </span>
                <span className="msbi-doc-nav__title">{nextDoc.title}</span>
              </Link>
            ) : (
              <span />
            )}
          </footer>
        </div>
      </div>
    </div>
  )
}

export default MsbiProjects
